import React, { useEffect, useRef, useState } from 'react';
import { BarChart2, Loader2, AlertCircle, ClipboardList, Download } from 'lucide-react';
import {
  TabContentWrapper,
  ExtractionHeader,
  ExtractionTitle,
  ExtractedBadge,
  StatusMessage,
  ExtractButton,
  LoadingState,
  EmptyExtractionState,
  AnalysisItemGrid,
  AnalysisItemCard,
  AnalysisItemHeader,
  AnalysisItemTitle,
  AnalysisScoreBadge,
  AnalysisProgressTrack,
  AnalysisProgressBar,
  AnalysisItemResponse,
} from './processing.styles';
import { useGetStatus, useAnalysis } from './hook/Useprocessing';
import { generateAnalysisReportPDF } from './AnalysisReport';

const IN_PROGRESS_MSGS = [
  'Data Analyses in progress!',
  'Analysis in Progress!',
  'Extraction is Pending!',
];

const isInProgressMsg = (msg = '') =>
  IN_PROGRESS_MSGS.some((m) => msg.toLowerCase().includes(m.toLowerCase()));

// initialAnalysisStatus is a one-time snapshot from the list load and can go
// stale — it stays 'PENDING' forever even after analysis completes on the
// server. We always re-check status fresh on every mount instead of
// permanently skipping the call. The prop is only used as an instant
// placeholder while that fresh check resolves.
const Analysis = ({ listId, initialAnalysisStatus, onRefetchList }) => {
  const { data: statusData, isLoading: isLoadingStatus } = useGetStatus(listId);

  const analysisStatus = statusData?.analysisStatus ?? initialAnalysisStatus;

  const { analysisMutation, analysisQuery } = useAnalysis(listId, analysisStatus);

  const reportRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Auto-trigger analyseData once status is COMPLETE and cache is empty
  useEffect(() => {
    if (
      analysisStatus === 'COMPLETE' &&
      !analysisQuery.data &&
      !analysisQuery.isFetching &&
      !analysisMutation.isPending &&
      !analysisMutation.isSuccess &&
      !analysisMutation.isError
    ) {
      analysisMutation.mutate();
    }
  }, [analysisStatus, analysisQuery.data, analysisQuery.isFetching]); // eslint-disable-line

  const analysisFromQuery = Array.isArray(analysisQuery?.data?.analysis)
    ? analysisQuery.data.analysis
    : null;
  const analysisFromMutation = Array.isArray(analysisMutation?.data?.analysis)
    ? analysisMutation.data.analysis
    : null;
  const analysisData = analysisFromQuery ?? analysisFromMutation ?? [];
  const hasAnalysisData = analysisData.length > 0;
  const isStaleFetching = analysisQuery.isFetching && analysisQuery.isPreviousData;

  // ── Detect "in progress" from either the mutation result or query error ───
  const mutationInProgress =
    analysisMutation.isSuccess &&
    isInProgressMsg(analysisMutation.data?.message ?? analysisMutation.data?.detail ?? '');

  const queryInProgress =
    analysisQuery.isError &&
    isInProgressMsg(analysisQuery.error?.message ?? analysisQuery.error?.detail ?? '');

  const analysisInProgress = mutationInProgress || queryInProgress;

  // Mirrors Extraction.jsx's handleGenerateReport — refetch the parent list
  // (and thus status) after every manual trigger, whether it succeeds or
  // errors, so the status prop passed back down next render is fresh.
  const handleGetAnalysis = () => {
    analysisMutation.mutate(undefined, {
      onSuccess: () => {
        onRefetchList?.();
      },
      onError: () => {
        onRefetchList?.();
      },
    });
  };

  const handleDownloadReport = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await generateAnalysisReportPDF({
        node: reportRef.current,
        listId,
        analysisData,
        fileName: `analysis-report-${listId}.pdf`,
      });
    } catch (err) {
      console.error('Failed to generate analysis report:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Waiting for status — only if we don't even have a placeholder yet ────
  if (isLoadingStatus && !initialAnalysisStatus) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Checking analysis status…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── Show loading when a stale cached query is being refreshed ──────────
  if (isStaleFetching) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching analysis data…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── Real data exists — always render it first, regardless of status flag ─
  if (hasAnalysisData) {
    return (
      <TabContentWrapper>
        <ExtractionHeader>
          <ExtractionTitle>Analysis Result</ExtractionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ExtractedBadge>
              <BarChart2 size={14} />
              Analysis Complete
            </ExtractedBadge>
            <ExtractButton type="button" onClick={handleDownloadReport} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <Loader2 size={14} className="spin" /> Generating…
                </>
              ) : (
                <>
                  <Download size={14} /> Download Report
                </>
              )}
            </ExtractButton>
          </div>
        </ExtractionHeader>

        <AnalysisItemGrid ref={reportRef}>
          {analysisData.map((item) => (
            <AnalysisItemCard key={item._id}>
              <AnalysisItemHeader>
                <AnalysisItemTitle>{item.title}</AnalysisItemTitle>
                <AnalysisScoreBadge $score={item.score}>{item.score}%</AnalysisScoreBadge>
              </AnalysisItemHeader>

              <AnalysisProgressTrack>
                <AnalysisProgressBar $score={item.score} />
              </AnalysisProgressTrack>

              <AnalysisItemResponse>{item.response}</AnalysisItemResponse>
            </AnalysisItemCard>
          ))}
        </AnalysisItemGrid>
      </TabContentWrapper>
    );
  }

  // ── In progress ───────────────────────────────────────────────────────────
  if (analysisMutation.isPending || analysisQuery.isLoading || analysisQuery.isFetching) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching analysis data…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── "Data Analyses in progress!" from API ────────────────────────────────
  if (analysisInProgress) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <Loader2 size={36} strokeWidth={1.2} className="spin" />
          <p style={{ margin: 0, fontWeight: 500 }}>Analysis In Progress</p>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
            Your documents are currently being analysed. Check back shortly or try again.
          </p>
          <ExtractButton
            type="button"
            onClick={handleGetAnalysis}
            disabled={analysisMutation.isPending}
          >
            {analysisMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Analysing…
              </>
            ) : (
              <>
                <BarChart2 size={14} /> Check Again
              </>
            )}
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Not yet complete — pending UI with manual trigger ────────────────────
  if (analysisStatus !== 'COMPLETE') {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <ClipboardList size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Analysis Pending</p>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
            Documents are still being analysed. You can trigger analysis manually when ready.
          </p>
          <ExtractButton
            type="button"
            onClick={handleGetAnalysis}
            disabled={analysisMutation.isPending}
          >
            {analysisMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Analysing…
              </>
            ) : (
              <>
                <BarChart2 size={14} /> Get Analysis
              </>
            )}
          </ExtractButton>
          {analysisMutation.isError && !analysisQuery.isFetching && (
            <StatusMessage $type="error">
              <AlertCircle size={14} />
              {analysisMutation.error?.message || 'Analysis failed. Please try again.'}
            </StatusMessage>
          )}
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Query-level error (non-progress) ─────────────────────────────────────
  if (analysisQuery.isError) {
    return (
      <TabContentWrapper>
        <StatusMessage $type="error" style={{ margin: '1.5rem 0' }}>
          <AlertCircle size={14} />
          {analysisQuery.error?.message || 'Failed to load analysis data.'}
        </StatusMessage>
      </TabContentWrapper>
    );
  }

  // ── COMPLETE but empty ────────────────────────────────────────────────────
  return (
    <TabContentWrapper>
      <EmptyExtractionState>
        <ClipboardList size={36} strokeWidth={1.2} />
        <p style={{ margin: 0 }}>No analysis data available yet.</p>
      </EmptyExtractionState>
    </TabContentWrapper>
  );
};

export default Analysis;
